import { useContext, useEffect, useState } from "react";
import "../../pages/css/Main.css";
import api from "../../AxiosConfig";
import UserContext from "../../context/UserContext";
import NotSubscribedWidget from "../../widget/NotSubscribedWidget";
import SubscriptionPlanSkeleton from "../../loading/SubscriptionPlanSkeleton";
import MainLayout from "../../layouts/MainLayout";

const SubscriptionPlanPage = () => {
  console.log("SubscriptionPlanPage component mounted");

  const { user, loading: userLoading } = useContext(UserContext);
  console.log("UserContext:", { user, userLoading });

  const [subscription, setSubscription] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const token = localStorage.getItem("jwtToken");
        const response = await api.get("/get-subscription", {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        setSubscription(response.data.message);
      } catch (error) {
        console.error("There was an error!", error);
        setError("Failed to fetch subscription data.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchSubscription();
  }, []);

  function formatDate(dateStr) {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleString('en-US', {
      timeZone: 'Asia/Singapore',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  }

  const getCardStatusInfo = () => {
    if (!user || user.role_name !== 'subscriber' || !subscription) {
      return null;
    }

    const now = new Date();
    const startDate = new Date(subscription.start_date);
    const endDate = new Date(subscription.end_date);

    if (subscription.status === 'expired') {
      return {
        text: 'EXPIRED',
        bgColor: 'bg-red-100',
        textColor: 'text-red-800',
        borderColor: 'border-red-300'
      };
    }

    if (subscription.status === 'cancelled') {
      return {
        text: 'CANCELLED',
        bgColor: 'bg-gray-100',
        textColor: 'text-gray-800',
        borderColor: 'border-gray-300'
      };
    }

    if (subscription.status === 'suspended') {
      return {
        text: 'SUSPENDED',
        bgColor: 'bg-yellow-100',
        textColor: 'text-yellow-800',
        borderColor: 'border-yellow-300'
      };
    }

    if (startDate > now) {
      const timeUntilStart = Math.ceil((startDate - now) / (1000 * 60 * 60 * 24));
      return {
        text: `STARTS IN ${timeUntilStart} DAY${timeUntilStart !== 1 ? 'S' : ''}`,
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-800',
        borderColor: 'border-blue-300'
      };
    }

    if (endDate <= now) {
      return {
        text: 'EXPIRED',
        bgColor: 'bg-red-100',
        textColor: 'text-red-800',
        borderColor: 'border-red-300'
      };
    }

    return null;
  };

  const cardStatus = getCardStatusInfo();

  if (isLoading) {
    return (
      <p>
        <SubscriptionPlanSkeleton />
      </p>
    );
  }

  if (error) {
    return <p>{error}</p>;
  }

  return (
    <MainLayout>
      <div className="py-2 sm:px-4">
        <div className="lg:px-12 lg:mt-12 md:mt-8 sm:px-4 md:px-6">
          <p
            style={{ fontFamily: "Red Hat Display" }}
            className="mb-8 lg:text-3xl md:text-2xl sm:text-2xl"
          >
            Account
          </p>
          {user.role_name === "free user" && <NotSubscribedWidget />}
          <div>
            <div className="lg:flex lg:flex-row md:flex md:flex-row sm:flex sm:flex-col lg:gap-24 md:gap-16 lg:mt-8 md:mt-8">
              <div className="gap-4 lg:flex lg:flex-col md:flex md:flex-col sm:flex sm:flex-row">
                <a href="/profile-page">
                  <p className="font-bold text-gray-400 lg:text-lg md:text-md sm:text-sm">
                    Profile
                  </p>
                </a>
                <a href="/account-settings">
                  <p className="font-bold text-gray-400 lg:text-lg md:text-md sm:text-sm">
                    Account Settings
                  </p>
                </a>
                <a href="/subscription-plan">
                  <p className="font-bold lg:text-lg md:text-md sm:text-sm text-md orange">
                    Subscription Plan
                  </p>
                </a>
              </div>

              <div className="flex flex-col gap-8">
                <p
                  style={{ fontFamily: "Red Hat Display" }}
                  className="lg:text-2xl md:text-xl sm:text-lg sm:mt-8"
                >
                  Your Current Plan
                </p>
                <div className="flex flex-col w-full max-w-lg gap-4 px-6 py-8 bg-white border-2 border-orange-600 rounded-lg relative">
                  {/* Status indicator for restricted access */}
                  {cardStatus && (
                    <div className="absolute -top-3 -right-3">
                      <div className={`px-3 py-1 rounded-full border text-xs font-bold ${cardStatus.bgColor} ${cardStatus.textColor} ${cardStatus.borderColor}`}>
                        {cardStatus.text}
                      </div>
                    </div>
                  )}

                  <div className="flex flex-row justify-between">
                    <p
                      className="lg:text-2xl md:text-xl sm:text-lg orange"
                      style={{ fontFamily: "Red Hat Display" }}
                    >
                      {subscription["subscription_name"]}
                    </p>
                    <div className="flex flex-col items-center">
                      <p className="lg:text-lg md:text-lg sm:text-md">
                        <b>
                          {subscription["currency"]} {subscription["price"]}
                        </b>
                      </p>
                      <p className="justify-center text-center align-middle lg:text-xs md:text-xs sm:text-xs orange w-36">
                        {subscription["end_date"] ? (
                          `One-time subscription till ${formatDate(subscription["end_date"])}`
                        ) : subscription["duration_days"] ? (
                          `${subscription["duration_days"]} days access`
                        ) : (
                          `${subscription["billing_frequency"]} subscription`
                        )}
                      </p>
                    </div>
                  </div>
                  <p className="lg:text-sm md:text-sm sm:text-xs">
                    {subscription["description"]}
                  </p>
                  <div className="flex flex-row gap-8">
                    <div className="flex flex-col gap-2">
                      <p className="lg:text-sm md:text-sm">
                        <b>Start time:</b>
                      </p>
                      <p className="lg:text-sm md:text-sm sm:text-xs">
                        {formatDate(subscription["start_date"] || subscription["joined_at"])}
                      </p>
                    </div>
                    <div className="flex flex-col gap-2">
                      <p className="lg:text-sm md:text-sm">
                        <b>End time:</b>
                      </p>
                      <p className="lg:text-sm md:text-sm sm:text-xs">
                        {formatDate(subscription["end_date"])}
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <a
                      href="/payment-plan"
                      className="block w-full bg-orange text-white py-2 px-4 rounded-md hover:bg-opacity-90 transition-colors text-center"
                      style={{ fontFamily: 'Red Hat Display' }}
                    >
                      View Payment Plans
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default SubscriptionPlanPage;
